/**
 * Step 5.1 — per-field Form 4 override log (Step 5 capture spec §2, §10.1).
 *
 * Every field on Form 4 that the system can pre-populate from the file record is
 * editable by the officer at 5.1. Accepting the pre-populated value is the
 * default; changing one is the exception — and because these are the fields a
 * court or appeal committee will scrutinize, each change is logged with the
 * original (system) value, the overridden value, the field, the officer, and the
 * timestamp.
 *
 * The override is scoped to the Form 4 generation event. It never rewrites the
 * underlying Stage 1–4 record: the log is evidence of what was served, not a
 * substitute for correcting the file at source.
 */
export type Form4FieldKey =
  | 'pid'
  | 'address'
  | 'ownerName'
  | 'ownerAddress'
  | 'localGovernment'
  | 'bylaw'
  | 'provision'
  | 'condition'
  | 'correctiveWork'
  | 'complianceDeadline'
  | 'penalty'
  | 'authority'
  | 'officerContact'
  | 'expertReportRef';

/** Where the pre-populated value comes from (Stages 2–4 spec §6 traceability). */
export type Form4FieldSource =
  | 'tombstone' // Stage 1 — PAN/PID tombstone record
  | 'entity_profile' // the local government's own profile
  | 'stage_2' // by-law / provision identified at review
  | 'site_visit' // Stage 3 observations
  | 'classification' // Stage 4 outcome
  | 'officer_profile' // the signing officer's contact block
  | 'static'; // prescribed language that does not vary by file

export interface Form4FieldMeta {
  key: Form4FieldKey;
  source: Form4FieldSource;
  /** Renders as a multi-line box rather than a single-line input. */
  multiline: boolean;
  /** Renders as a date input; the value is an ISO `YYYY-MM-DD` string. */
  date?: boolean;
  /** Discreet statutory reference, where the field hangs off one. */
  statuteRef?: string;
}

/**
 * The overridable Form 4 fields, in the order they appear on the prescribed
 * form. This is the single definition shared by the API (which derives the
 * system values and diffs the submission against them) and the officer portal
 * (which renders one editable control per entry).
 */
export const FORM4_FIELDS: Form4FieldMeta[] = [
  { key: 'pid', source: 'tombstone', multiline: false },
  { key: 'address', source: 'tombstone', multiline: false },
  { key: 'ownerName', source: 'tombstone', multiline: false },
  { key: 'ownerAddress', source: 'tombstone', multiline: false },
  { key: 'localGovernment', source: 'entity_profile', multiline: false },
  { key: 'bylaw', source: 'stage_2', multiline: false },
  { key: 'provision', source: 'classification', multiline: false, statuteRef: 's. 131' },
  { key: 'condition', source: 'site_visit', multiline: true },
  { key: 'correctiveWork', source: 'classification', multiline: true },
  {
    key: 'complianceDeadline',
    source: 'classification',
    multiline: false,
    date: true,
    statuteRef: 's. 132(2)',
  },
  { key: 'penalty', source: 'classification', multiline: true, statuteRef: 's. 139' },
  { key: 'authority', source: 'static', multiline: true, statuteRef: 's. 137' },
  { key: 'officerContact', source: 'officer_profile', multiline: true },
  { key: 'expertReportRef', source: 'classification', multiline: false, statuteRef: 's. 138' },
];

export const FORM4_FIELD_KEYS: Form4FieldKey[] = FORM4_FIELDS.map((f) => f.key);

/** A partial map of Form 4 field values, keyed by field. */
export type Form4FieldValues = Partial<Record<Form4FieldKey, string>>;

/** One logged override — exactly the five columns the spec requires. */
export interface Form4OverrideEntry {
  id: string;
  fileId: string;
  /** The generated Form 4 this override was applied to, when still on file. */
  formId: string | null;
  field: Form4FieldKey;
  /** The pre-populated value as it stood on the file before Form 4 was opened. */
  originalValue: string;
  /** What the officer entered instead. */
  newValue: string;
  officerId: string | null;
  /** The officer's display name, snapshotted at override time. */
  officerName: string | null;
  /** ISO timestamp of the change. */
  createdAt: string;
}

/**
 * What the 5.1 screen needs to render: the system values to pre-populate the
 * editable form with, plus the log of every override recorded so far.
 */
export interface Form4OverrideState {
  fields: Form4FieldValues;
  entries: Form4OverrideEntry[];
}

/**
 * Keep only the recognised field keys with string values.
 *
 * `fieldValues` arrives as a free-form object: this repo deliberately does not
 * use `@Type`/`@ValidateNested` (reflect-metadata is not loaded in the frontend
 * bundle), so the DTO validates the shape as an object and the structure is
 * checked here, at the boundary, the same way `SaveStageDataDto` does.
 */
export function pickForm4FieldValues(input: unknown): Form4FieldValues {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const bag = input as Record<string, unknown>;
  const out: Form4FieldValues = {};
  for (const key of FORM4_FIELD_KEYS) {
    const value = bag[key];
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}
